
import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle, type ReactNode, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent } from "react";

import type { ScanSessionPurpose } from "store/scan-session/types";

import { useDispatch, useSelector } from 'store/hooks'
import { endScanSesion, startScanSession } from 'store/scan-session/actions'
import {
  IValidationScanSessionMessageCallbackParams,
  IValidationStartScanSessionCallbackParams,
} from 'store/scan-session/types'

import { useSnackbar, useEncodingDevice } from "contexts";
// VERA-455 — a scanned BOX LABEL receives itself (see `handleAddPackage`), so this block needs
// the same mutation + permission check the receipt prompt uses. Both are already in this
// module's graph via `ConfirmReceivePackageModal` below, so neither is a new dependency.
import { useCanAccess } from 'hooks';
import { useReceivePackage } from 'hooks/api';
import { createApiCall } from 'services';
import { getErrorMessage } from 'utils';
import { Link as RouterLink } from 'react-router-dom';
import { ROUTES } from 'routes';
import { TagIcon, ArrowRightIcon, InfoIcon, SmartphoneNfcIcon, CheckIcon, ChevronDownIcon, BoxIcon, XIcon, TriangleAlertIcon } from 'lucide-react'
import { useQRCode } from "next-qrcode";

import {alpha, Stack, useTheme, styled, Box, Typography, Divider, CircularProgress, Collapse, Grow, Link, IconButton } from "@mui/material";

import { Button } from "@repo/ui";
import { ReaderSetupPanel } from "components/display/ReaderSetupPanel/ReaderSetupPanel";
// The §34 scan-source surface, shared so this hand-roll and `ScanConnectModal`'s cannot drift
// from the canonical `ScanSourceRow` treatment before VRG-322 converges them onto the component
// itself. Direct file import for the same cycle reason as `ReaderSetupPanel` above.
import { scanSourceSurfaceSx } from "components/display/ScanSourceRow/ScanSourceRow";
// Direct file import for the same cycle reason as `ReaderSetupPanel` above.
import { TagPreview } from "components/display/TagPreview/TagPreview";
// Direct file import, NOT the `components` barrel — this file is part of that barrel, so
// importing back through it is a cycle (same note as `ReaderSetupPanel` above).
import { InfoTooltip } from "components/feedback/InfoTooltip/InfoTooltip";
// VERA-455 — the receipt prompt that resolves an `at_vera` refusal. Direct file import for the
// same cycle reason as the imports above.
import {
  ConfirmReceivePackageModal,
  type ReceivePackagePrompt,
} from "components/feedback/modals/ConfirmReceivePackageModal/ConfirmReceivePackageModal";

interface ScanSessionQRCode {
  registerDeviceURL: string,
}

export const ScanSessionQRCode = (props: ScanSessionQRCode) => {
  const { registerDeviceURL } = props;
  const { Canvas } = useQRCode();
  const theme = useTheme();


const StyledQRScan = styled(Stack)({
  "@keyframes y": {
    to: {
      transform: 'translateY(-100%)',
      top: '100%',
    }
  },
  "@keyframes x": {
    '33%': {
      clipPath: 'inset(-100px 0 0 0)',    
    },
    '50%': {
      clipPath: 'inset(0 0 0 0)',    
    },
    '83%': {
      clipPath: 'inset(0 0px -100px 0)',
    }
  },
  width: '100%',
  height: '100%',
  position: 'relative',
  '::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    // width: 1,
    height: 1,
    width: '100%',
    opacity: .5,
    background: theme.palette.brand[500],
    boxShadow: `0 0 70px 15px ${theme.palette.brand[500]}`,
    clipPath: `inset(0)`,
    animation: "y 2.5s ease-in-out infinite alternate, x 5s ease-in-out infinite",
  }
});

  return (
    <Grow in={Boolean(registerDeviceURL)}>
      <StyledQRScan 
        // ref={registerDeviceURL} 
        justifyContent="center" 
        alignItems="center" 
        mt={6}
        spacing={3}>
        <Canvas
          text={registerDeviceURL}
          options={{
            errorCorrectionLevel: 'M',
            margin: 3,
            scale: 4,
            width: 200,
            color: {
              dark: '#000000',
              light: '#FFFFFF',
            },
          }}
        />
      </StyledQRScan>
    </Grow>
  );
}

/**
 * Height of a scan-source header row. Both rows are pinned to it so they are identical
 * regardless of what their right-hand side holds ("Connected" + glyph vs. a lone chevron).
 *
 * 42px is not arbitrary: it is what MUI's `<Radio>` used to impose here (9px padding around a
 * 24px icon). The radio was removed when the two sources became concurrent rather than a
 * choice, and it took the row height with it — nothing else was setting one, so both rows
 * collapsed to bare text height and to DIFFERENT heights from each other.
 */
const SOURCE_ROW_HEIGHT = 42

/**
 * A scan-source header row is a §34 SELECTABLE CARD, not an action row: it carries a persistent
 * per-source state, so hover is a brand TINT only and the brand BORDER is reserved for "chosen"
 * ("tint = considering, border = chosen — never use a brand border for hover on a selectable
 * card"). It previously took the action-row treatment — brand border + soft ring on hover — which
 * is precisely the collision that rule names.
 *
 * The hover/focus treatment therefore lives on the CONTAINER (which owns the border a tint has to
 * sit inside), via the shared `scanSourceSurfaceSx`, and this only carries what belongs to the
 * row itself. `outline: none` is required: the row holds the `tabIndex`, so without it the UA's
 * own focus ring draws on top of the container's deliberate one.
 *
 * The 1px transparent border is gone with the border-on-hover it existed to stop shifting.
 */
const sourceRowTriggerSx = () => ({
  minHeight: SOURCE_ROW_HEIGHT,
  cursor: 'pointer',
  userSelect: 'none' as const,
  outline: 'none',
})

/**
 * The counter's label, per session PURPOSE.
 *
 * The number under this label is NOT "tags scanned", which is what it used to claim. It is the
 * count of tags this session can still ACT on — the quantity the footer button operates on — so a
 * tag that arrives and is refused ("Assigned already") shows in the list below without moving it.
 * Scanning one already-certified tag therefore read "Tags scanned: 0" next to a visibly scanned
 * tag, which says the opposite of what happened. The label was wrong; the number was right, and
 * stays exactly as it is. Making it count arrivals instead would put it in open disagreement with
 * the button it feeds, which is the worse of the two bugs.
 *
 * KEYED ON `purpose` BECAUSE THIS BLOCK IS SHARED AND THE COUNT MEANS A DIFFERENT THING IN EACH
 * CONTEXT. A certificate session stages tags to mint from; a filter session stages them to find
 * records with. "Scanned for creation" is simply false in the second, and this is the third
 * purpose-blind string in this component (see `SCAN_FEEDBACK.existing`, whose "already in use with
 * another certificate" has the same defect), so it is fixed as a mapping rather than a string.
 *
 * `Record<ScanSessionPurpose, …>` is the point of the shape: adding a member to the union fails to
 * compile here until this file states what that purpose calls its count. A new purpose cannot
 * silently inherit the certificate wording.
 */
const SCANNED_COUNT_LABEL: Record<ScanSessionPurpose, string> = {
  certificate: 'Scanned for creation',
  // Parallel construction with the above, and deliberately naming what the tags are FOR rather
  // than what they become: a filter session mints nothing, it narrows a list.
  filter: 'Scanned for search',
  // VERA-455. Present because the `Record` demands it, not because this component renders a
  // receive session today — `ReceivePackageModal` owns that surface and counts BOXES, not tags,
  // which is why it does not reuse this block. Kept truthful rather than plausible: if this
  // block is ever pointed at a receive session, the one thing that must not happen is it
  // inheriting "Scanned for creation" and telling an operator their receipt will mint
  // certificates. That is exactly the silent-fallthrough this map exists to make impossible.
  receive: 'Scanned to receive',
}

/**
 * A caller that does not state its purpose gets the NEUTRAL label, never the certificate one.
 * Vague and correct beats specific and wrong, which is the whole lesson of the three instances
 * above. `purpose` is optional on the props, so this is reachable: `CreateCertificatesModal`
 * states `purpose="certificate"` to opt into the specific wording.
 */
const scannedCountLabel = (purpose?: ScanSessionPurpose) =>
  (purpose && SCANNED_COUNT_LABEL[purpose]) || 'Scanned tags'

/**
 * Enter/Space on a row trigger — but ONLY when the row itself has focus.
 *
 * Without the `target !== currentTarget` guard, a keypress on a control INSIDE the row (the
 * "Show QR" link is a real `<button>`) activates that control and then bubbles here, toggling
 * the panel a second time and landing back where it started.
 */
const onRowTriggerKeyDown = (toggle: () => void) => (e: ReactKeyboardEvent) => {
  if (e.target !== e.currentTarget) return
  if (e.key !== 'Enter' && e.key !== ' ') return
  e.preventDefault()
  toggle()
}

/**
 * The `(i)` glyph sits INSIDE a row that is now itself a toggle, so its click has to be stopped
 * here or reaching for the explanation collapses the panel you were reading about.
 */
const stopRowToggle = (e: ReactMouseEvent) => e.stopPropagation()

/**
 * Per-source readiness: a filled brand circle with a check when the source is connected, a
 * hollow outline when it is not.
 *
 * A CHECK, not the radio that used to sit here. A radio means "pick one of these", which is
 * what the old UI wrongly implied — the reader and the phone both feed one session at the
 * same time. A check means "this one is ready", which is a status, and two sources can both
 * be ready at once. It also restores the row height the radio was silently providing.
 */
const SourceStatus = ({ connected }: { connected: boolean }) => {
  const theme = useTheme()
  return (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: connected ? theme.palette.brand[500] : 'transparent',
        border: connected ? 'none' : `1.5px solid ${theme.palette.grey[400]}`,
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
      }}
    >
      {connected && <CheckIcon size={13} strokeWidth={3} color={theme.palette.common.white} />}
    </Box>
  )
}

interface ScanSessionBlockProps {
  singleTag?: boolean
  onScanTags?: (tags: string[]) => void
  packages?: boolean
  showScans?: boolean
  productId?: string
  /**
   * VRG-265 — what the session this block opens is FOR.
   *
   * `certificate` (default) is the minting flow. `filter` opens a READ-ONLY session: the API
   * refuses create-certificates, add-package and replace-certificate-tag on it, and it needs no
   * `productId` — which is the whole reason scan-to-filter can exist. Company scoping is
   * identical either way, so a filter session still refuses another company's tag.
   */
  purpose?: ScanSessionPurpose
  /**
   * Presentational only — swaps copy for the replace/add flows. Never sent to the server.
   * VRG-388 added `'add'`, which shares the replace flow's feedback map (an already-certified
   * tag means "reach for another", not "dead end") but has its own scan prompt.
   */
  intent?: 'create' | 'replace' | 'add'
  onPendingCountChange?: (count: number) => void
  showEncoderOption?: boolean
  // VRG-233 — hide the "Scan method" (phone / reader) selector rows. Used by the
  // create-certificate wizard's Step 2, where the method was already chosen in Step 1
  // and this block stays mounted (so the underlying scan session survives the step
  // change) but only needs to show the "Tags scanned" summary. Defaults to true so
  // every other caller (ReplaceTagModal, SearchByScanModal, and CreateCertificatesModal's
  // own single-screen Single mode) is unaffected.
  showMethodSelector?: boolean
  // VERA-790 — extra hint rendered beneath the scan prompt (e.g. "…or tap a package to add all its tags").
  scanHint?: ReactNode
  // VRG-233 — when set, the "Tags scanned" pill shows this label (e.g. "Group 1")
  // instead of the generic Tag/Box icon, and `groupCount` (falling back to the
  // internal running total) instead of the flat session total — so a multi-group
  // create-certificate-group session can show a per-group count instead of an
  // all-groups-combined one.
  groupLabel?: string
  groupCount?: number
  // VRG-233 — fires with the running list of tag ids that are actually eligible to
  // become NEW certificate-group members (i.e. NOT a tag that already has a
  // certificate) whenever it changes. Deliberately separate from `onScanTags`
  // (which includes every scanned id, including already-certified ones, for
  // backward compatibility with existing callers) so group composition can never
  // silently include an invalid tag.
  onGroupableTagsChange?: (tagIds: string[]) => void
  // VRG-233 — fires whenever the phone (QR) connection state changes. The create-
  // wizard uses this to gate its Step 1 → Step 2 "Next" button: the QR is only shown
  // in Step 1 (showMethodSelector), so advancing before the phone actually connects
  // would land on a Step 2 with nothing to scan yet and no way back to the QR short
  // of "Back". (A hardware reader's connection state is already available to any
  // caller directly via useEncodingDevice(), so it doesn't need piping through here.)
  onConnectionChange?: (phoneConnected: boolean) => void
  /**
   * The SESSION was ended from somewhere else — today, the phone's "End session" control.
   *
   * The owning modal decides what that means for it (Create Certificates closes). Without this
   * the modal would sit open over a dead session: the QR is stale, the phone has stood down,
   * and every further scan has nowhere to land — a surface that looks live and is not.
   */
  onSessionEnded?: () => void
}

export interface ScanSessionBlockRef {
  createCertificates: () => Promise<void>
  /**
   * VRG-268 — repoint a certificate onto a replacement tag.
   *
   * The replacement tag must be scanned into this session — there is deliberately no way to
   * supply one by uid (typed, or read by a desk reader). See the removal note on
   * `ScanSessionService.replaceCertificateTag`.
   *
   * VRG-275 — `delivery` says what happens to the OLD certificate's live state after this
   * call: `'fitted'` (default) goes live immediately, same as before this field existed;
   * `'posted'` retires the old tag but leaves the new one HELD until the owner confirms
   * receipt. See `ReplaceTagModal`'s delivery choice for the operator-facing side of this.
   */
  replaceCertificateTag: (
    certificateId: string,
    options?: { reason?: string; delivery?: 'fitted' | 'posted' },
  ) => Promise<void>
  /**
   * VRG-388 — bind a scanned tag to a certificate that has NONE.
   *
   * The ADDITIVE sibling of `replaceCertificateTag`, and deliberately not a flag on it. Replace
   * retires the chip currently bound and refuses a tagless certificate; this attaches a first
   * chip and refuses one that is already tagged. The server enforces both directions, so the
   * worst a mixed-up call site can do is get a refusal.
   *
   * No `reason` and no `delivery`: there is no old tag to explain away and none whose liveness
   * could be described, so neither field would be recording anything true.
   *
   * The tag is SCANNED into this session, same as replace — there is no way to supply one by
   * uid, for the same reason (see that method's note).
   */
  addCertificateTag: (certificateId: string) => Promise<void>
  // VERA-790 — bulk-add every tag in a package to this session (as if each were scanned). The SSE
  // stream updates the pending list + count; returns the tallies for a caller-side message.
  // VERA-455 — `receipt` is set when the box was refused because its tags are still `at_vera`.
  // NOTHING was added in that case, so a caller must not report a tally: this block raises the
  // receipt prompt itself and re-runs the call once the receipt lands.
  addPackage: (packageId: string) => Promise<{
    added: number
    existing: number
    total: number
    receipt?: { packageId: string | null; packageName: string | null; tagCount: number }
    notice?: string
  }>
}

// Per-scan inline feedback shown in the "Tags scanned" area (replaces the old per-scan toasts).
// A good scan clears it; these surface right under the count pill.
type ScanFeedbackKind =
  | 'error'
  | 'duplicate'
  | 'duplicate-package'
  | 'existing'
  // A whole PACKAGE landed. Positive, not a problem — see TONE below.
  | 'package'
  // The scan worked but the tag isn't the one this product is configured for.
  | 'mismatch'
  // VERA-455 — refused because our data says Vera still holds the stock, which is exactly the
  // case where the operator is holding it. A refusal (the tag did NOT go in), so it keeps the
  // error tone; what makes it different is that it carries its own way out.
  | 'not-received'

/**
 * How loudly a feedback message reads.
 *
 * The note used to have exactly one look — red text, red-tinted count pill — because the only
 * things that spoke were failures. Two outcomes now need to speak that are NOT failures: a
 * package expanding (good) and a default-tag mismatch (worked, but check what you're holding).
 * Rendering either in the ownership-refusal's red would teach operators that red no longer
 * means "that tag did not go in", which is the one thing it has to keep meaning.
 */
type ScanFeedbackTone = 'error' | 'warning' | 'info'
const TONE: Record<ScanFeedbackKind, ScanFeedbackTone> = {
  error: 'error',
  duplicate: 'error',
  'duplicate-package': 'error',
  existing: 'error',
  package: 'info',
  mismatch: 'warning',
  'not-received': 'error',
}

/**
 * VERA-455 — the browser's wording for the `at_vera` refusal, and the ONE place this component
 * deliberately does not echo the server's own sentence.
 *
 * The server says "Tag has not shipped yet", which is the accurate statement of the data and
 * the right thing to show a surface that can only report. This surface can ACT: it raises the
 * receipt prompt and offers the link below. Rendering "has not shipped" beside a "Receive
 * package" control would read as a contradiction to someone holding the box, so the sentence
 * is restated in terms of the thing the operator can now do something about.
 *
 * The phone keeps the server sentence, and that divergence is intended rather than drift: it
 * has no receive flow, so an actionable sentence there would offer an action it cannot perform.
 */
const NOT_RECEIVED_FEEDBACK = "This tag's package hasn't been received into your inventory yet."

/** An open receipt prompt, plus what to do once the receipt lands. */
type ReceiptPrompt = ReceivePackagePrompt & { source: 'tag' | 'package' }

/**
 * The feedback LINE is transient state, not a mode — so it expires on its own.
 *
 * It used to persist until the next scan replaced it, which meant a refusal from ten tags ago
 * was still on screen (and still tinting the count pill red) long after the operator had moved
 * on to good tags. The line then stopped meaning "the tag you just held did not go in" and
 * started meaning "something failed at some point", which is not actionable. The phone already
 * expires its own copy of this message; these are the browser's half of the same behaviour.
 *
 * MIN_VISIBLE is the guard the phone learned the hard way (`ERROR_MIN_VISIBLE_MS`): a good scan
 * landing milliseconds behind a refusal clears the line, so an operator working quickly could
 * see a refusal flash and vanish before it could be read — the exact failure the distinct
 * refusal messages exist to prevent. For MIN_VISIBLE after a refusal appears, nothing quieter
 * may replace it. A NEWER refusal still wins immediately.
 *
 * 5s (vs. the phone's 6s) per the brief. MIN_VISIBLE matches the phone at 2s.
 *
 * SCOPE: this expires the transient LINE only. Persistent row state — the `Assigned already`
 * row in *Scanned tags*, its tint and its `View` button — lives in `sessionTags` and is
 * untouched: the line reports the last scan, the row is a standing fact about the session.
 */
const FEEDBACK_AUTO_CLEAR_MS = 5000
const FEEDBACK_MIN_VISIBLE_MS = 2000

/**
 * VRG-275 — the REPLACE flow says different things for the same events, and this used to be
 * the bug RESUME listed as open: `ScanSessionBlock` took a `purpose` prop and never branched
 * on it, so a replace session told the operator a tag was "already in use with another
 * certificate" as if they were minting a new one. Correct sentence, wrong flow.
 *
 * `intent` is PRESENTATIONAL ONLY and deliberately separate from `purpose`, which the server
 * reads to decide session semantics. Adding a 'replace' purpose would have meant changing the
 * API's own union to alter a sentence.
 */
const SCAN_FEEDBACK_REPLACE: Partial<Record<'error' | 'duplicate' | 'duplicate-package' | 'existing', string>> = {
  // In a replace session, "already in use" is not a dead end — it is a wrong-tag-in-hand
  // moment, and the operator has a drawer of other tags. Say what to do next.
  existing: 'Tag already certified, try another',
}

const SCAN_FEEDBACK: Record<'error' | 'duplicate' | 'duplicate-package' | 'existing', string> = {
  error:
    "Scan failed. Try again by holding your device close to the Vera tag. Ensure your device's NFC is enabled.",
  duplicate: "You've already scanned this tag",
  // Re-scanning a BOX. Told "…this tag", an operator goes looking for a tag they never
  // touched. Which one applies is decided by what the SERVER says the scan was
  // (`viaPackage`, derived from the `/pkg/<uuid>` URL shape) — never from the chip family,
  // since plain 213 chips are used as product tags too.
  'duplicate-package': "You've already scanned this package",
  existing: 'This tag is already in use with another certificate',
}

/**
 * The exact sentence every server-side refusal ends with (see `SUPPORT_LINE` in
 * `scan-session.service.ts`). Matched verbatim so "contact support" can be rendered as a link
 * to the platform's support page — a refusal that offers no way out is just an accusation.
 * The phone does the same thing with the same sentence, via `Linking.openURL`.
 */
/**
 * Replace-flow copy where it differs; the shared sentence everywhere else.
 *
 * VRG-388 — `'add'` takes the REPLACE map, not the create one. The distinction the map draws
 * is not add-vs-replace, it is "you are minting from a batch" vs "you are binding ONE tag to a
 * certificate that already exists". In the second case an already-certified tag means the
 * operator has the wrong chip in hand and should reach for another, which is exactly what
 * `SCAN_FEEDBACK_REPLACE` says. The prompt string differs and is branched at its own call site.
 */
const SCAN_FEEDBACK_FOR = (intent: 'create' | 'replace' | 'add') =>
  intent === 'create' ? SCAN_FEEDBACK : {...SCAN_FEEDBACK, ...SCAN_FEEDBACK_REPLACE}

const SUPPORT_SENTENCE = 'If you believe this is an error, contact support.'
const SUPPORT_LINK_TEXT = 'contact support'

/**
 * Renders a feedback message, turning the support sentence's "contact support" into an in-app
 * link. Any message that doesn't carry the sentence renders unchanged.
 */
const ScanFeedbackMessage = ({ message }: { message: string }) => {
  const at = message.indexOf(SUPPORT_SENTENCE)
  if (at === -1) return <>{message}</>
  const linkAt = SUPPORT_SENTENCE.indexOf(SUPPORT_LINK_TEXT)
  return (
    <>
      {message.slice(0, at)}
      {SUPPORT_SENTENCE.slice(0, linkAt)}
      {/* Was ROUTES.HELP_CENTER, which VRG-224 removed along with the ReadMe.io iframe it
          rendered. "Contact support" is now a typed request rather than a page, so the link
          points at the intake (/support/new) — the same replacement that route's removal note
          names for the old LIVE_SUPPORT entry. */}
      <Link component={RouterLink} to={ROUTES.SUPPORT_NEW} underline="always" color="inherit">
        {SUPPORT_LINK_TEXT}
      </Link>
      {SUPPORT_SENTENCE.slice(linkAt + SUPPORT_LINK_TEXT.length)}
      {message.slice(at + SUPPORT_SENTENCE.length)}
    </>
  )
}

export const ScanSessionBlock = forwardRef<ScanSessionBlockRef, ScanSessionBlockProps>((props, ref) => {
    const {
      packages = false,
      singleTag = false,
      showScans = true,
      onScanTags = () => { /* no-op */ },
      productId,
      purpose,
      intent = 'create',
      onPendingCountChange,
      showEncoderOption = true,
      showMethodSelector = true,
      scanHint,
      groupLabel,
      groupCount,
      onGroupableTagsChange,
      onConnectionChange,
      onSessionEnded,
    } = props

    // Real USB reader presence, via the Vera Reader Helper (was a hardcoded stub). When a
    // PC/SC reader (e.g. ACR1552) is plugged into the helper, this is true and its scans
    // stream in via `subscribeScan`.
    const { isConnected: readerConnected, subscribeScan } = useEncodingDevice()
    const [phoneConnected, setPhoneConnected] = useState<boolean>(false)
    const [scanDeviceURL, setScanDeviceURL] = useState<string | null>(null)
    // Decide the scan method during the FIRST render: if a hardware reader is already
    // connected at mount, start on 'reader'. Otherwise the phone Collapse opens with the QR,
    // then the reader-detect effect below flips to 'reader' post-paint and collapses it —
    // a visible QR flash. Choosing here (not in an effect) avoids opening that section at all.
    // The reader and the phone are INPUT SOURCES, not modes, and sources are additive —
    // both feed this one session at the same time. There is deliberately no selection.
    //
    // The old radio implied an exclusive choice it only half-enforced: it gated the reader
    // subscription below, but the phone posts to `mobile-scan` addressed by session id,
    // server-side, and never knew the radio existed. So picking "Reader" did not stop the
    // phone, while picking "Phone" DID stop the reader — an asymmetry nothing in the UI
    // disclosed. The real workflow wants both anyway (reader for a tray on the desk, phone
    // for tags already on boxes), so the choice was pure friction.
    //
    // What remains is purely about SCREEN SPACE: the QR is large. Null = follow the default
    // below; true/false = the user said so explicitly.
    const [qrExpandedByUser, setQrExpandedByUser] = useState<boolean | null>(null)
    // The reader panel is an accordion the OPERATOR opens, closed by default. It used to be
    // forced open whenever no reader was connected (`Collapse in={!readerConnected}`), which
    // meant the common case — no reader, phone only — permanently spent ~260px of the modal
    // on setup instructions for hardware the operator may not own, with no way to dismiss it.
    const [readerExpanded, setReaderExpanded] = useState(false)
    const [scannedTagIds, setScannedTagIds] = useState<string[] | []>([])
    // VRG-233 — subset of scannedTagIds that are valid NEW certificate-group members
    // (excludes tags that came back 'existing', i.e. already certified elsewhere).
    const [groupableTagIds, setGroupableTagIds] = useState<string[]>([])
    // The reviewable pending list: what is actually in this session, newest first, each row
    // removable. `scannedTagIds` stays as-is because it is the parent's contract; this carries
    // the per-tag detail a row needs (its outcome, and the certificate already using it).
    const [sessionTags, setSessionTags] = useState<
      Array<{
        tagId: string
        status: 'pending' | 'existing' | 'created'
        certificateId?: string
        viaPackage?: boolean
        // WHICH box a package-sourced row came from. `viaPackage` alone marks a row as
        // not-hand-scanned; these make it attributable when a session holds two boxes.
        packageId?: string
        packageName?: string
        // What the tag CAN DO ("Verify", "Connect", …) — the only name a BLANK tag has, since
        // a pending row resolves to no certificate and every row in a session shares the
        // modal's one product. Capability only, never the chip family (see `TagPreview`).
        // Null/absent for a tag with no catalog SKU; the row falls back to a neutral label.
        capability?: string | null
      }>
    >([])
    const [removingTagId, setRemovingTagId] = useState<string | null>(null)
    /**
     * The feedback line, so it can be pulled into view when it has something to say.
     *
     * The modal body scrolls (measured at 1280x720: scrollHeight 706 vs clientHeight 516), and
     * the scan area sits near its bottom — so a message that arrives while the operator is
     * looking anywhere else, or whose second line falls past the fold, is simply never read.
     * `block: 'nearest'` scrolls the MINIMUM needed and does nothing when it is already
     * visible, so this never yanks the modal during normal scanning.
     */
    const feedbackRef = useRef<HTMLDivElement | null>(null)
    const [tagScanState, setTagScanState] = useState<string>(singleTag? 'single-loading' : 'inactive')
    const [createdCount, setCreatedCount] = useState<number>(0)
    const [existingCount, setExistingCount] = useState<number>(0)
    const [errorCount, setErrorCount] = useState<number>(0)
    const [pendingCount, setPendingCount] = useState<number>(0)
    // Inline per-scan feedback in the "Tags scanned" area — null shows the neutral prompt; a
    // failed scan, an in-session re-scan, or an already-certified tag show a red note instead.
    // `certificateId` rides along on the 'existing' kind so the note can offer a way to LOOK at
    // the certificate that is already using the tag — without it the operator is told the tag is
    // taken and given nothing to act on.
    const [scanFeedback, setScanFeedback] = useState<
      { kind: ScanFeedbackKind; message: string; certificateId?: string; receipt?: ReceiptPrompt } | null
    >(null)
    /**
     * VERA-455 — the package a receipt would act on, or null when no prompt is open.
     *
     * `source` is what "then proceed" means once the receipt lands, and the two paths differ:
     *  - `package`: the operator scanned a BOX, so the expansion is re-run and the tags land.
     *    Nothing was added on the refused attempt, so there is nothing to undo first.
     *  - `tag`: the operator tapped ONE tag. The scan's cryptographic proof was consumed by the
     *    attempt that was refused, and there is no way to add a tag by uid (see the removal note
     *    on `replaceCertificateTag`), so the honest continuation is to ask for another tap.
     *    Expanding the whole package instead would add ~100 tags the operator never scanned.
     */
    const [receiptPrompt, setReceiptPrompt] = useState<ReceiptPrompt | null>(null)
    // When the line currently on screen appeared, and whether it was a refusal — the two
    // facts the MIN_VISIBLE guard needs. Refs, not state, for the reason every other ref in
    // this file is one: the SSE handler is registered ONCE on mount and would read forever
    // the first render's state. `ref.current` is always current.
    const feedbackShownAtRef = useRef<number>(0)
    const feedbackIsErrorRef = useRef<boolean>(false)
    const feedbackClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    /**
     * The ONLY way the feedback line is set or cleared. Every call site goes through here so
     * the expiry timer and the min-visible guard cannot be forgotten at one of them.
     *
     * A new scan RE-ARMS the timer rather than queueing behind it, so the 5s is always measured
     * from the message actually on screen — otherwise a run of scans would leave the last one
     * inheriting whatever was left of the first one's countdown.
     *
     * `force` bypasses the min-visible guard, for a clear the OPERATOR asked for (removing a
     * row). The guard exists to stop the app from talking over itself; it should never stop a
     * person from dismissing something deliberately.
     */
    const showScanFeedback = useCallback((
      next: { kind: ScanFeedbackKind; message: string; certificateId?: string; receipt?: ReceiptPrompt } | null,
      opts?: { force?: boolean },
    ) => {
      const nextIsError = next ? TONE[next.kind] === 'error' : false
      // Nothing quieter than a refusal may replace one inside its min-visible window — and
      // that includes `null`, which is what a good scan sends. See FEEDBACK_MIN_VISIBLE_MS.
      if (
        !opts?.force &&
        !nextIsError &&
        feedbackIsErrorRef.current &&
        Date.now() - feedbackShownAtRef.current < FEEDBACK_MIN_VISIBLE_MS
      ) {
        return
      }
      feedbackIsErrorRef.current = nextIsError
      feedbackShownAtRef.current = next ? Date.now() : 0
      setScanFeedback(next)

      if (feedbackClearTimerRef.current) {
        clearTimeout(feedbackClearTimerRef.current)
        feedbackClearTimerRef.current = null
      }
      if (next) {
        feedbackClearTimerRef.current = setTimeout(() => {
          feedbackClearTimerRef.current = null
          feedbackIsErrorRef.current = false
          feedbackShownAtRef.current = 0
          setScanFeedback(null)
        }, FEEDBACK_AUTO_CLEAR_MS)
      }
    }, [])
    // Which certificate is already using a given tag. Kept so the "View" way out survives a
    // RE-scan of the same tag: the second tap takes the duplicate branch, which knows only the
    // uid, and the operator would otherwise be told "already scanned" with no way to see what
    // it is on. A ref for the same reason as seenTagIdsRef — the SSE handler is registered once.
    const certificateByTagRef = useRef<Map<string, string>>(new Map())
    // What each tag in the session counted AS, so a removal knows which counter to bring down.
    const statusByTagRef = useRef<Map<string, 'pending' | 'existing' | 'created'>>(new Map())
    // A package arrives as one SSE result PER TAG, so the message has to be assembled from
    // them. Without this the modal said nothing at all while its counter jumped by N — the
    // phone showed "Package scanned · N tags added" and the browser showed silence.
    const packageRunRef = useRef<{count: number; timer: ReturnType<typeof setTimeout> | null}>({
      count: 0,
      timer: null,
    })
    // Has the user explicitly chosen a scan method? Once they have, don't auto-switch
    // them (e.g. away from phone) just because a reader connects.
    // Tags already counted THIS session — so re-scanning the same tag (common when scanning
    // a batch quickly) shows an "already scanned" note instead of double-counting + re-toasting
    // it as new. A ref (not state) because the SSE handler is registered once and reads stale
    // state; ref.current is always current. Cleared when the session resets (unmount below).
    const seenTagIdsRef = useRef<Set<string>>(new Set())
    // Same reason as every other ref here: the SSE handler is registered ONCE on mount, so a
    // prop read directly inside it would be forever the first render's. The owning modal's
    // close handler is re-created every render, which would make that stale callback close a
    // modal using state from before the session even had tags in it.
    const onSessionEndedRef = useRef<(() => void) | undefined>(onSessionEnded)
    useEffect(() => {
      onSessionEndedRef.current = onSessionEnded
    }, [onSessionEnded])

    const { open: notification } = useSnackbar()
    // VERA-455 — see `handleAddPackage`. A box scan receives itself; without the permission it
    // falls back to the prompt, whose disabled confirm names what is missing (§46).
    const receivePackage = useReceivePackage()
    const { isHasAccess: canReceive } = useCanAccess({ action: 'tags_inventory_receive' })
    const dispatch = useDispatch()
    const theme = useTheme()
    const scanSessionId = useSelector(state => state.scanSession.scanSessionId)

    useEffect(() => {
      // Code to run on mount goes here
      handleCreateScanSession()
  
      // Optional cleanup function (runs on unmount)
      return () => {
        dispatch(endScanSesion())
        setScannedTagIds([])
        setGroupableTagIds([])
        setSessionTags([])
        seenTagIdsRef.current.clear()
        certificateByTagRef.current.clear()
        statusByTagRef.current.clear()
        if (packageRunRef.current.timer) clearTimeout(packageRunRef.current.timer)
        packageRunRef.current = { count: 0, timer: null }
        // The expiry timer MUST die with the component. This block lives in a modal that is
        // routinely closed mid-run (Create Certificates closes on success, and on the phone
        // ending the session), so an armed timeout would otherwise outlive its own component
        // and fire `setScanFeedback` at an unmounted tree.
        if (feedbackClearTimerRef.current) clearTimeout(feedbackClearTimerRef.current)
        feedbackClearTimerRef.current = null
        feedbackIsErrorRef.current = false
        feedbackShownAtRef.current = 0
        setScanDeviceURL(null)
        setPhoneConnected(false)
        setTagScanState('inactive')
        setScanFeedback(null)
      };
    }, []);

    // Notify parent component when pending count changes
    useEffect(() => {
      onPendingCountChange?.(pendingCount);
    }, [pendingCount, onPendingCountChange]);

    /**
     * VERA-455 — collapse the QR when a phone PAIRS, and only then.
     *
     * The QR's whole job is the handoff. Once a phone is on the session it has been done, and the
     * panel is ~218px of a scrolling modal spent on an instruction that has been followed —
     * pushing the tag list, the feedback line and the reader row below the fold at the exact
     * moment scanning starts.
     *
     * ON THE TRANSITION, NOT ON THE STATE, and that distinction is the entire fix. The default
     * below (`qrDefault`) used to carry a `!phoneConnected` term, which collapsed the QR only for
     * an operator who had never touched the toggle: once `qrExpandedByUser` was latched — and the
     * WHOLE ROW is a toggle, so it latches on a stray click, not just a deliberate "Show QR" — the
     * `??` never reached the default again and the panel stayed open through pairing. Which is the
     * reported bug: "Connected" beside a QR nobody needs.
     *
     * The inverse error is worse and is why this is not `phoneConnected && collapse`: an operator
     * pairing a SECOND device (legitimate — sources are additive, and a phone that died leaves
     * "Connected" latched with no disconnect event) would have the QR re-hidden under them on
     * every render. So the edge is latched in a ref, `setQrExpandedByUser(false)` fires once, and
     * a deliberate re-open afterwards stands: there is no second transition to undo it.
     *
     * Re-delivery is safe without extra guarding — `setPhoneConnected(true)` on an already-true
     * value bails out of the render entirely, so a reconnect event cannot re-run this.
     */
    const phoneWasConnectedRef = useRef(false)
    useEffect(() => {
      if (phoneConnected && !phoneWasConnectedRef.current) setQrExpandedByUser(false)
      phoneWasConnectedRef.current = phoneConnected
    }, [phoneConnected]);

    // Bring the scan feedback into view whenever it says something. See feedbackRef.
    useEffect(() => {
      if (!scanFeedback) return
      // 'center', and INSTANT. Both were arrived at by measurement, not taste:
      //  - 'nearest' moved the body 4px and left the message's second line (the one carrying
      //    "View certificate") 10px past the fold — "nearest" was satisfied while the
      //    actionable half of the message stayed unreadable.
      //  - 'end' did not move the body at all (scrollTop stayed 0): a smooth scroll requested
      //    mid-render, while this same state update was also adding a row to the list below,
      //    never completed.
      //  - 'center' with the default instant behavior takes the body from 0 to 190 and puts the
      //    whole line, link included, inside the scroll viewport. Verified in the browser.
      feedbackRef.current?.scrollIntoView({ block: 'center' })
    }, [scanFeedback]);

    // Report the accumulated tag list to the parent when it changes — done here (not in
    // the once-registered message handler, which closes over stale state) mirroring the
    // pending effect above. `onScanTags` is a stable setter from the parent.
    useEffect(() => {
      onScanTags(scannedTagIds);
    }, [scannedTagIds]);

    // VRG-233 — same reporting pattern as onScanTags above, for the group-eligible subset.
    useEffect(() => {
      onGroupableTagsChange?.(groupableTagIds);
    }, [groupableTagIds]);

    // VRG-233 — report the phone (QR) connection state so a wizard-shaped caller can
    // gate advancing past the step that shows the QR.
    useEffect(() => {
      onConnectionChange?.(phoneConnected);
    }, [phoneConnected]);

    // Stream USB-reader scans into this session (when the reader method is selected). The
    // Vera Reader Helper reads a tag's `e=` token off the PC/SC reader and hands it here;
    // we relay it to the same session a phone would use, so it flows through the identical
    // decode → pending → SSE path (the SSE handler above then updates the counts).
    useEffect(() => {
      // Still gated on showEncoderOption: when the reader row is hidden entirely, the
      // reader is not an offered source and must not ingest. Previously this fell out of
      // the mode logic (the effect forced scanMethod to 'phone' when the option was off);
      // with the mode gone it has to be stated.
      if (!scanSessionId || !showEncoderOption) return
      const unsub = subscribeScan(async (e) => {
        // VERA-790 — a package BOX HANDLE (/pkg/<id>) is a plain 213, not a product SDM tag. The
        // Create Certificates modal's own package listener handles it (adds the whole package to
        // the session). Don't relay it to reader-scan, which would fail to decode it and surface a
        // false "scan failed". Product tags (/ver…) fall through as normal.
        if (/\/pkg\/[0-9a-f-]{36}/i.test(e)) return
        try {
          const response = await createApiCall({
            method: 'POST',
            url: `/admins/admin/scan-session/${scanSessionId}/reader-scan`,
            data: { e },
          })
          // createApiCall RESOLVES on 4xx (it doesn't throw), so a rejected scan would be
          // silent otherwise — surface the failure. On success the "Tag … Scanned" toast
          // comes from the SSE handler; here we only report failures.
          const status = (response as any)?.status
          if (typeof status === 'number' && (status < 200 || status >= 300)) {
            // The server message can carry a full stack trace — log it for debugging but
            // never render raw internals in the toast; show a short, clean message.
            const detail = (response as any)?.data?.message
            if (detail) console.error('[reader-scan] failed:', detail)
            showScanFeedback({ kind: 'error', message: SCAN_FEEDBACK.error })
          }
        } catch (err) {
          console.error('[reader-scan] error:', err)
          showScanFeedback({ kind: 'error', message: SCAN_FEEDBACK.error })
        }
      })
      return unsub
    }, [scanSessionId, showEncoderOption, subscribeScan]);


    // START SCAN_SESSION
    const startScanSessionCallback = (params: IValidationStartScanSessionCallbackParams) => {
      const { success, registerDeviceURL } = params
      if (success) {
        console.log(registerDeviceURL)
        setScanDeviceURL(registerDeviceURL)
      }
    }
  
    /**
     * A package expands into one result per tag, so its message is COUNTED UP rather than set
     * once. Each viaPackage result bumps the running total and re-renders the same sentence;
     * a short quiet period then closes the run, so a second box scanned later starts over
     * instead of continuing the first one's tally.
     */
    const notePackageTag = () => {
      const run = packageRunRef.current
      run.count += 1
      const n = run.count
      showScanFeedback({
        kind: 'package',
        message: `Package scanned · ${n} tag${n === 1 ? '' : 's'} added`,
      })
      if (run.timer) clearTimeout(run.timer)
      run.timer = setTimeout(() => {
        run.count = 0
        run.timer = null
      }, 2500)
    }

    const scanSessionMessageHandler = (params: IValidationScanSessionMessageCallbackParams) => {
      const { tagId, deviceRegistered, status, certificateId, viaPackage, packageId, packageName, warning, capability } = params

      // THE SESSION IS OVER — ended from the phone. Not a tag event: there is nothing to count
      // and nothing to add. Hand it straight to the owner, which closes the modal. Checked
      // before the tagId branch because the event carries no tagId and would otherwise be
      // silently dropped by it.
      if (params.sessionEnded) {
        onSessionEndedRef.current?.()
        return
      }

      // VERA-455 — A BOX WAS RECEIVED BY THE PHONE, on this session.
      //
      // The reader path already toasts this from `handleAddPackage`, because a receipt's effect
      // is entirely off screen (it moves possession on the Inventory tab) and the toast policy
      // gives an invisible action a toast. A PHONE-scanned box does the same act, so it owes the
      // desk the same report — otherwise the two instruments produce the same write and only one
      // of them says so.
      //
      // Also checked before the `tagId` branch: like `sessionEnded`, this event carries no tagId
      // and would be dropped in silence by it. The tags themselves still arrive afterwards as
      // ordinary `viaPackage` scan results, which is what updates the count and the list — this
      // reports only the half of the tap that has no visible surface.
      //
      // `notification` is a `useCallback` from the snackbar context, so it is stable across the
      // life of this once-registered handler; nothing here reads state directly.
      const receivedPackage = params.packageReceived
      if (receivedPackage?.packageId) {
        notification({
          type: 'success',
          title: `${receivedPackage.packageName?.trim() || 'Package'} received into your inventory`,
        })
        return
      }

      if (tagId) {
        // A tag taken back OUT of the session (removed here, or from another surface). Not a
        // scan: it must decrement, drop the row, and let the same tag count again if re-scanned.
        if (status === 'removed') {
          // Which counter to decrement is read from the ref, NOT from inside a state updater —
          // an updater must stay pure (React may run it twice), so it can't also be the place
          // the sibling counters are adjusted.
          const previousStatus = statusByTagRef.current.get(tagId)
          if (!previousStatus) return
          statusByTagRef.current.delete(tagId)
          seenTagIdsRef.current.delete(tagId)
          certificateByTagRef.current.delete(tagId)
          if (previousStatus === 'existing') setExistingCount(v => Math.max(0, v - 1))
          else if (previousStatus === 'created') setCreatedCount(v => Math.max(0, v - 1))
          else setPendingCount(v => Math.max(0, v - 1))
          setScannedTagIds(prev => prev.filter(id => id !== tagId))
          setSessionTags(prev => prev.filter(t => t.tagId !== tagId))
          // `force`: the operator removed this row themselves, so the line describing it should
          // go with it. The min-visible guard exists to stop the app talking over itself, not
          // to make a person wait out a message they have already acted on.
          showScanFeedback(null, { force: true })
          return
        }

        // Registered ONCE on mount (handed to the saga in the []-deps effect), so it closes over
        // INITIAL state — use functional updates + refs, never read state directly. Feedback is
        // shown inline in "Tags scanned" (below), no per-scan toasts.

        // Failed scan: placeholder id, its own event. Show the inline error; don't count as a tag.
        if (status === 'error') {
          setErrorCount(v => v + 1)

          // VERA-455 — the one refusal with an in-app way out. Branch on the PRESENCE of the
          // structured field, never on the message text: the sentence is pinned verbatim by
          // the docs' scan test-plan, so matching it would silently drop the way out the first
          // time someone reworded it.
          //
          // A null `packageId` is a real case and deliberately NOT prompted: the tag belongs to
          // no package, so `transitionPackageTags` would match nothing and report `{updated: 0}`
          // in silence. The server sends its own explanatory sentence (with the support link)
          // for that case, and it falls through to the generic branch below — a prompt that
          // cannot do anything is worse than no prompt.
          const notReceived = params.notReceived
          if (notReceived?.packageId) {
            const receipt: ReceiptPrompt = {
              packageId: notReceived.packageId,
              packageName: notReceived.packageName,
              tagCount: notReceived.tagCount,
              // `viaPackage` decides the continuation, not an assumption about which surface
              // sent this — the phone can scan a box handle into this same session.
              source: viaPackage ? 'package' : 'tag',
            }
            // BOTH: the prompt is the one-click path the ticket asks for, and the line is what
            // remains if the operator cancels it. Without the line, cancelling would leave the
            // refusal with no way back except re-tapping the tag.
            showScanFeedback({ kind: 'not-received', message: NOT_RECEIVED_FEEDBACK, receipt })
            setReceiptPrompt(receipt)
            return
          }

          // The SERVER's own wording when it sent one. It was collapsing every refusal into
          // the generic "Scan failed…" string, so an ownership rejection ("Tag not assigned to
          // this company") — the one message that tells an operator the tag is fine but the
          // session is wrong — was thrown away and replaced with advice to hold the phone
          // closer. The phone already surfaces the server text; the modal now matches it.
          //
          // It read `update`, a variable that does not exist in this scope — so every refusal
          // threw a ReferenceError before it could be shown, and the operator saw nothing at
          // all. The field is on `params`, which the saga hands through verbatim from the SSE
          // event (see `pushScanResult`'s payload).
          const serverMessage = params.error
          showScanFeedback({
            kind: 'error',
            message: typeof serverMessage === 'string' && serverMessage.trim()
              ? serverMessage
              : SCAN_FEEDBACK.error,
          })
          return
        }
        // Re-scan of something already in THIS session → inline "already scanned", no
        // double-count. Which noun it uses follows what the server says was scanned.
        if (seenTagIdsRef.current.has(tagId)) {
          const knownCertificateId = certificateByTagRef.current.get(tagId)
          showScanFeedback(
            // A re-tap of a tag we ALREADY know is certified says the useful thing — and keeps
            // the "View" way out. Told merely "you've already scanned this", the operator is
            // left with the same dead end the View button exists to remove.
            knownCertificateId
              ? { kind: 'existing', message: SCAN_FEEDBACK.existing, certificateId: knownCertificateId }
              : viaPackage
              ? { kind: 'duplicate-package', message: SCAN_FEEDBACK_FOR(intent)['duplicate-package'] }
              : { kind: 'duplicate', message: SCAN_FEEDBACK.duplicate },
          )
          return
        }
        seenTagIdsRef.current.add(tagId)
        setScannedTagIds(prev => (prev.includes(tagId) ? prev : [...prev, tagId]))
        if (status === 'created' || status === 'existing' || status === 'pending') {
          statusByTagRef.current.set(tagId, status)
          // Newest first: the row an operator most likely wants to undo is the one they just
          // scanned, so it sits at the top of the list rather than N rows down.
          setSessionTags(prev => [
            { tagId, status, certificateId, viaPackage, packageId, packageName, capability },
            ...prev,
          ])
        }

        if (status === 'created') {
          setCreatedCount(v => v + 1)
          // A good scan clears the line — subject to the min-visible guard, so it cannot wipe a
          // refusal the operator has not had time to read yet.
          showScanFeedback(null)
          setGroupableTagIds(prev => (prev.includes(tagId) ? prev : [...prev, tagId]))
        } else if (status === 'existing') {
          // Verified, but the tag already has a certificate → can't create a duplicate.
          // NOT added to groupableTagIds — an already-certified tag can never become a
          // NEW certificate group's member (VRG-233).
          setExistingCount(v => v + 1)
          if (certificateId) certificateByTagRef.current.set(tagId, certificateId)
          showScanFeedback({ kind: 'existing', message: SCAN_FEEDBACK.existing, certificateId })
        } else if (status === 'pending') {
          setPendingCount(v => v + 1)
          showScanFeedback(null)
          setGroupableTagIds(prev => (prev.includes(tagId) ? prev : [...prev, tagId]))
          if (!singleTag) setTagScanState('active')
        }

        // A package speaks LAST, and wins, because one box scan is one event: the per-tag
        // results it expands into must not each get to overwrite the message with their own.
        if (viaPackage) {
          notePackageTag()
        } else if (warning && status !== 'existing') {
          // Warn-and-allow. The scan landed — it just isn't the tag this product is set up for.
          // Deliberately not shown over an 'existing' note, which is the stronger fact (that
          // tag can't be certified at all, so which tag-shop product it came from is moot).
          showScanFeedback({ kind: 'mismatch', message: warning })
        }
      }
      if (deviceRegistered) {
        // The "Connected" source-status chip in this modal is the confirmation — no toast.
        // (Idempotent: the SSE can re-deliver deviceRegistered on reconnect.)
        setPhoneConnected(true)
      }
    }
    
  
    const handleCreateScanSession = () => {
      dispatch(startScanSession(startScanSessionCallback, scanSessionMessageHandler, productId, purpose))
    }

    const handleCreateCertificates = async () => {
      if (pendingCount === 0 || !scanSessionId) return;

      console.log(`🔄 Creating certificates for session ${scanSessionId} with ${pendingCount} pending tags`)
      try {
        const response = await createApiCall({
          method: 'POST',
          url: `/admins/admin/scan-session/${scanSessionId}/create-certificates`,
        });

        const result = response.data;
        const created = result?.certificates?.length ?? 0;
        const failed = result?.errors?.length ?? 0;

        // The API returns success:true even when EVERY tag errored — so gate on how many
        // were actually created, not just `success`. Otherwise a total failure showed a
        // "Successfully created 0 certificates" toast and auto-closed the modal.
        if (result?.success && created > 0) {
          notification(
            failed > 0
              ? { type: 'warning', title: `Created ${created} certificate${created === 1 ? '' : 's'}, ${failed} failed`, description: result?.errors?.[0]?.error }
              : { type: 'success', title: `Successfully created ${created} certificate${created === 1 ? '' : 's'}` },
          );

          // Reset pending count
          // Note: createdCount is already incremented via SSE events from the backend
          setPendingCount(0);

          // Update tag scan state
          if (singleTag) {
            setTagScanState('single-active');
          } else {
            setTagScanState('active');
          }
        } else {
          // Nothing created — surface the real reason and keep the modal open (throw).
          throw new Error(result?.errors?.[0]?.error || 'No certificates were created');
        }
      } catch (error) {
        console.error('Error creating certificates:', error);
        // Let the parent surface the user-facing error — it catches the re-throw and
        // notifies (notifying here too produced a duplicate error toast).
        throw error;
      }
    }

    /**
     * Take a mis-scanned tag back out of the session.
     *
     * Per-row, from the list the operator is already reviewing before Create Certificates —
     * which is the natural moment to correct a mis-scan, and which subsumes an "undo last"
     * (the most recent row is the first one). No confirmation: nothing is destroyed, and
     * re-scanning the tag costs one tap, so a dialog here would be friction on a recoverable
     * action (working-conventions §38 — an `X` removes from a set, a trash can destroys a
     * record). The server's removal event is what actually updates this list and the phone's
     * counter, so both surfaces move together rather than the browser guessing locally.
     */
    const handleRemoveTag = async (tagId: string) => {
      if (!scanSessionId || removingTagId) return
      setRemovingTagId(tagId)
      try {
        const response = await createApiCall({
          method: 'POST',
          url: `/admins/admin/scan-session/${scanSessionId}/remove-tag`,
          data: { tagId },
        })
        // createApiCall RESOLVES on 4xx rather than throwing, so a refusal would otherwise
        // leave the row sitting there with no explanation.
        const status = (response as any)?.status
        if (typeof status === 'number' && (status < 200 || status >= 300)) {
          throw new Error((response as any)?.data?.message || 'Could not remove that tag')
        }
      } catch (err) {
        console.error('[remove-tag] failed:', err)
        // One `title`, no `description`: this file's other notification() calls pass a
        // description that `NotificationProps` does not declare (six standing type errors),
        // and there is no reason to add a seventh for a message that fits on one line.
        notification({ type: 'error', title: getErrorMessage(err) || 'Could not remove that tag' })
      } finally {
        setRemovingTagId(null)
      }
    }

    /**
     * `retry` marks the call made AFTER a receipt has landed, and it is a loop guard, not
     * bookkeeping: if the server still reports `at_vera` (a receipt that reported success but
     * moved nothing, a tag added to the box mid-flight) the auto-receive below would otherwise
     * receive-and-retry forever against a box that will not budge. One attempt, then the prompt.
     */
    const handleAddPackage = async (packageId: string, retry = false): Promise<{
      added: number
      existing: number
      total: number
      receipt?: { packageId: string | null; packageName: string | null; tagCount: number }
      notice?: string
    }> => {
      if (!scanSessionId) {
        throw new Error('Scan session not initialized')
      }
      const response = await createApiCall({
        method: 'POST',
        url: `/admins/admin/scan-session/${scanSessionId}/add-package`,
        data: { packageId },
      })
      // The SSE stream (reader-scan listener) pushes the added tags into the session, so the
      // pending count + running list update on their own — no local mutation needed here.
      const data = response.data as {
        added: number
        existing: number
        total: number
        receipt?: { packageId: string | null; packageName: string | null; tagCount: number }
        notice?: string
      }

      // VERA-455 — the box's tags are still `at_vera`, so NOTHING was added and the server is
      // asking for a receipt rather than refusing outright.
      //
      // NO PROMPT ON THIS PATH. Every caller of this method got here because the operator
      // scanned a PACKAGE LABEL, and receiving that package is precisely the act the label scan
      // implies: it is the box, it is in their hands, and the receipt marks the box. Asking
      // "receive this package?" of someone who just scanned that package's own label is a
      // confirmation with no decision in it — the answer is always yes, so it is pure friction
      // and it trains operators to click through the prompt that DOES carry a decision.
      //
      // That prompt survives, on the single-tag path only (see the `notReceived` branch in the
      // SSE handler): there the operator tapped ONE tag and the receipt moves up to ~100
      // siblings, which is a genuine leap past what they did and needs consent. The split is
      // read from which branch we are in, never inferred from the payload — the two paths are
      // structurally separate and the server distinguishes them for us (a package refusal comes
      // back in this reply, a tag refusal arrives on the SSE with `notReceived`).
      if (data.receipt?.packageId) {
        const receipt = {
          packageId: data.receipt.packageId,
          packageName: data.receipt.packageName,
          tagCount: data.receipt.tagCount,
        }
        if (!retry && canReceive) {
          try {
            await receivePackage.mutateAsync({ packageId: receipt.packageId })
            // A TOAST, not the inline feedback line. The receipt's effect is entirely off
            // screen — it moves possession on the Inventory tab — so per the toast policy an
            // invisible action gets one. The visible half (the tags arriving in the session) is
            // reported by the caller's own tally, which now runs because the retry below
            // returns a result with no `receipt` on it.
            notification({
              type: 'success',
              title: `${receipt.packageName?.trim() || 'Package'} received into your inventory`,
            })
            return await handleAddPackage(packageId, true)
          } catch (err) {
            // Fall through to the prompt rather than swallowing it. The receipt failed, so the
            // box is still refused, and the prompt is the only surface that can show WHY and
            // let the operator try again.
            console.error('[receive-package] auto-receive failed:', err)
          }
        }
        // No permission (§46: the prompt's confirm is disabled with a tooltip naming
        // `tags_inventory_receive`), or the automatic receipt failed. Either way the operator
        // needs the surface that can explain it.
        setReceiptPrompt({ ...receipt, source: 'package' })
      }
      return data
    }

    /**
     * VRG-268 (replace) + VRG-388 (add) — the two ways a tag becomes bound to a certificate.
     *
     * ONE function with a `mode`, not two copies. Everything that makes this awkward to write
     * is identical between them: the session/pending-count preconditions, the `createApiCall`
     * resolves-on-4xx trap, and the ~10-case error-message mapping that turns a server string
     * into something an operator can act on. That mapping is the part worth not duplicating —
     * a second copy would drift, and it is the only thing standing between the operator and a
     * bare "failed" (§36).
     *
     * What differs is only: the endpoint, whether there is a body, and the copy. Note that the
     * SAFETY difference is NOT expressed here at all — the server refuses an already-tagged
     * certificate on `add` and a tagless one on `replace`, in separate use-cases. This function
     * choosing the wrong URL produces a refusal, not a wrong write.
     */
    const handleBindCertificateTag = async (
      certificateId: string,
      options?: {
        mode?: 'add' | 'replace'
        reason?: string
        delivery?: 'fitted' | 'posted'
      },
    ) => {
      const mode = options?.mode ?? 'replace'
      const isAdd = mode === 'add'
      const verb = isAdd ? 'add' : 'replace'

      if (!scanSessionId) {
        notification({
          type: 'error',
          // VRG-607 — "not initialized" is programmer vocabulary for a state the
          // reader has no model of. What they need to know is that the scanner
          // connection dropped and a refresh restores it.
          //
          // Also: `description` is NOT a field on NotificationProps (it declares
          // `text`), so this body was silently dropped and the toast rendered a
          // bare title. Renaming it makes the sentence visible for the first time.
          title: 'Lost connection to the scanner',
          text: 'Please refresh the page and try again.',
        });
        return;
      }

      if (pendingCount === 0) {
        notification({
          type: 'error',
          title: 'No tag scanned',
          description: `Please scan exactly one tag to ${verb} the certificate tag`,
        });
        return;
      }

      if (pendingCount > 1) {
        notification({
          type: 'error',
          title: `${pendingCount} tags scanned`,
          description: `Please scan only one tag to ${verb}`,
        });
        return;
      }

      console.log(`🔄 ${isAdd ? 'Adding' : 'Replacing'} certificate tag for certificate ${certificateId} with session ${scanSessionId}`)

      try {
        const response = await createApiCall({
          method: 'POST',
          url: isAdd
            ? `/admins/admin/scan-session/${scanSessionId}/add-certificate-tag/${certificateId}`
            : `/admins/admin/scan-session/${scanSessionId}/replace-certificate-tag/${certificateId}`,
          // VRG-388 — `add` sends NO body. There is no old tag, so there is nothing to give a
          // reason for and nothing whose liveness `delivery` could describe: a tag being
          // attached for the first time cannot be "posted to the owner while the old one is
          // retired", because there is no old one and no owner. Sending either field would be
          // recording a fact about a workflow that did not happen.
          data: isAdd
            ? {}
            : {
                reason: options?.reason?.trim() || undefined,
                // VRG-275 — default 'fitted' matches the API contract's default, so an
                // omitted delivery (any caller that hasn't been updated yet) keeps
                // today's exact behaviour rather than silently going unset.
                delivery: options?.delivery ?? 'fitted',
              },
        });

        const result = response.data;
        console.log(`📋 ${isAdd ? 'Add' : 'Replace'} tag response:`, result)

        // `createApiCall` RESOLVES on 4xx/5xx rather than rejecting, so a permission refusal or
        // a validation error arrives here looking like a successful call whose body happens to
        // carry a `message`. Without this explicit status check the failure fell through to
        // `throw new Error('Tag replacement failed')` below, discarding the server's actual
        // reason — the operator was told "failed" and never told WHY (not your tag, tag not
        // received yet, already bound elsewhere), which are the cases they can act on.
        if (response.status && response.status >= 400) {
          const error: any = new Error(
            result?.message || (isAdd ? 'Adding the tag failed' : 'Tag replacement failed'),
          );
          error.response = response;
          throw error;
        }

        if (result.success) {
          notification({
            type: 'success',
            title: isAdd ? 'Tag added successfully' : 'Tag replaced successfully',
            description: isAdd
              ? 'This certificate can now be reached by scanning the tag'
              : 'The certificate has been updated with the new tag',
          });

          // Reset pending count and update created count
          setPendingCount(0);
          setCreatedCount(prev => prev + 1);

          // Update tag scan state
          setTagScanState(singleTag ? 'single-active' : 'active');
        } else {
          throw new Error(isAdd ? 'Adding the tag failed' : 'Tag replacement failed');
        }
      } catch (error: any) {
        console.error(`Error ${isAdd ? 'adding' : 'replacing'} certificate tag:`, error);

        // Extract error message from API response
        // VRG-607 — the `|| error?.message` rung leaked axios's "Request failed
        // with status code 400" straight into `description` below, and the final
        // fallback "Unknown error occurred" is a log line: it reports that WE do
        // not know, which is our problem and not something the reader can use.
        // The chain now ends in an instruction instead.
        //
        // `errorMessage` is still substring-matched below to map known server
        // strings to human copy, so the server's own message is kept as the
        // first rung — that matching is what those branches exist for.
        const errorMessage =
          error?.response?.data?.message ||
          'Please try again, or contact support if it keeps happening.';

        // Provide specific error messages based on common scenarios
        let title = isAdd ? 'Failed to add tag' : 'Failed to replace tag';
        let description = errorMessage;

        if (errorMessage.includes('not assigned to your company')) {
          // VRG-268: the VRG-250 ownership guard. The replacement must come from this
          // company's own stock, which is the single most likely refusal here and was
          // previously shown as a bare "Failed to replace tag".
          title = 'Tag is not yours';
          description = 'This tag is not assigned to your company. Use a tag from your own inventory.';
        } else if (errorMessage.includes('has not shipped')) {
          title = 'Tag not received yet';
          description = 'This tag is still with Vera. Receive the package into your inventory before using it as a replacement.';
        } else if (errorMessage.includes('already has a tag')) {
          // VRG-388 — the `add` refusal. Reached when the certificate acquired a tag between
          // the modal opening and the confirm (another operator, or another tab). The action
          // the operator wants now is a DIFFERENT one, so name it rather than saying "failed".
          title = 'This certificate already has a tag';
          description = 'Use Replace tag to move it onto a different tag.';
        } else if (errorMessage.includes('does not have a tag assigned')) {
          // The mirror-image refusal, from `replace`. Same cause, opposite direction.
          title = 'This certificate has no tag yet';
          description = 'Use Add tag to attach one.';
        } else if (errorMessage.includes('permission') || error?.response?.status === 403) {
          title = 'You do not have permission';
          // Both operations are gated on the SAME permission, whose label is "Replace tag" —
          // so name that label even in the add flow. Saying "the 'Add tag' permission" would
          // send the operator looking for a row that does not exist in the role editor.
          description = `${isAdd ? 'Adding' : 'Replacing'} a tag needs the 'Replace tag' permission. Ask an admin to grant it.`;
        } else if (errorMessage.includes('scan session')) {
          title = 'Scan session expired';
          description = 'Please close this dialog and start a new tag replacement';
        } else if (errorMessage.includes('not available')) {
          title = 'Tag not available';
          description = 'The scanned tag is already assigned to another certificate';
        } else if (errorMessage.includes('same tag')) {
          title = 'Same tag scanned';
          description = 'The scanned tag is already assigned to this certificate';
        } else if (errorMessage.includes('not belong')) {
          title = 'Access denied';
          description = 'This certificate does not belong to your company';
        } else if (errorMessage.includes('Network Error')) {
          // VRG-607 — "Network error" as a TITLE is the library's own words. The
          // body already says the useful thing, so the title should too.
          title = "Can't reach Vera";
          description = 'Please check your internet connection and try again.';
        }

        notification({
          type: 'error',
          title,
          description,
        });

        throw error;
      }
    }

    // Expose functions to parent via ref
    useImperativeHandle(ref, () => ({
      createCertificates: handleCreateCertificates,
      replaceCertificateTag: (certificateId, options) =>
        handleBindCertificateTag(certificateId, { ...options, mode: 'replace' }),
      // VRG-388. Separate ref method rather than an exposed `mode` flag, so a caller cannot
      // reach the destructive path by passing the wrong string — the two are named operations
      // at every layer, and `mode` never crosses this boundary.
      addCertificateTag: certificateId =>
        handleBindCertificateTag(certificateId, { mode: 'add' }),
      addPackage: handleAddPackage
    }), [handleCreateCertificates, handleBindCertificateTag, handleAddPackage, pendingCount, scanSessionId]);

    // TODO: NOTE: should we move this to constants/messages. Better readability here but probabaly best there
    const tagScanStateMessages = {
      'single-inactive': "Hold your phone near the item's tag location to scan the new Vera Tag",
      'single-loading': "Hold your phone near the item's tag location to scan the new Vera Tag",
      'single-active': "New Vera Tag scanned. Click 'Replace tag' to complete the process and save changes.",
      'inactive' : 'Start scanning tags by holding your device close to a Vera Tag',
      'active': 'Continue scanning tags or click the "Create Certificates" button to process pending tags',
      'group-active': 'Continue scanning tags or click the "Add Next Group" button',
      'group-inactive': 'Start scanning tags by holding your device close to a Vera Tag',
      'failed-scan': "Scan failed. Try again by holding your device closer to a Vera Tag. Ensure your device's NFC is enabled."
    }

    // Whether the QR panel is open. Note this governs SPACE ONLY — the phone can pair and
    // scan regardless, and the reader ingests regardless; nothing here disables a source.
    //
    // Collapsed once a phone is paired (there is nothing left to scan), and collapsed by
    // default when a reader is already connected, since the operator has a working path and
    // the QR would just push the tag list off screen. Otherwise open, because pairing a
    // phone is then the most likely next action. An explicit Show/Hide always wins.
    // `showEncoderOption && readerConnected`, not bare `readerConnected`. The reader being
    // plugged in is irrelevant where the reader is not an OFFERED source: SearchByScanModal
    // passes showEncoderOption={false}, so it hides the reader row and disables reader ingest
    // — yet the QR still defaulted collapsed, leaving that modal with no visible way to scan
    // at all. Mirrors `canShowScanSummary` below, which already got this right.
    //
    // The explicit toggle now wins even once a phone is paired. Previously `phoneConnected`
    // short-circuited to false AND the Show/Hide control was inside the not-connected branch,
    // so a phone that backgrounded or died left "Connected" latched with no QR and no control
    // to bring it back — there is no disconnect event, so the only recovery was reopening the
    // whole modal.
    //
    // `phoneConnected` is deliberately NOT a term here any more (VERA-455): pairing is an EDGE,
    // and it is handled as one by the transition effect above, which sets `qrExpandedByUser`
    // directly. Leaving it in the default too would put two mechanisms on one behaviour, and the
    // weaker of the two — this one is unreachable the moment the row has ever been clicked, which
    // is exactly the case where it was needed.
    const qrDefault = !(showEncoderOption && readerConnected)
    const qrOpen = qrExpandedByUser ?? qrDefault

    const canShowScanSummary = showScans && (phoneConnected || (showEncoderOption && readerConnected))
    // Total tags seen this session — drives the count pill's value, colour, and its per-scan
    // "pop" animation (the pill is keyed on this so a new scan re-mounts + replays the animation).
    // Errors are NOT tags. The handler above already refuses to count them as one — it
    // returns before `seenTagIdsRef`, so a failed scan adds no id — and it dedupes re-taps of
    // the same tag. Then this line added `errorCount` straight back on top, and unlike the
    // other three that counter increments per EVENT, so tapping one foreign tag three times
    // pushed the pill to 3 while no tag had been added at all.
    //
    // The phone counts unique tag ids in the session, so the two surfaces disagreed on what
    // the number meant: 6 here against 3 on the device for the same run. Same definition now
    // — tags in this session. Failures keep their own "Errors: N" line below.
    //
    // `existingCount` IS EXCLUDED. An already-certified tag is rejected: Create Certificates
    // skips it, so counting it made the pill promise certificates the batch would not mint —
    // scan four tags where one is already certified and the pill reads 4 while 3 are created.
    // The number's job is to predict the batch, so it counts what the batch will use.
    // The tag keeps its row in *Scanned tags* (red, `Assigned already`) — it is information
    // the operator needs, it just isn't a tally. `existingCount` survives for that row's
    // decrement on removal and for the debug breakdown, which reports all four separately.
    // The phone applies the same rule (`countedTagIds`), so the two surfaces still agree.
    const totalScanned = createdCount + pendingCount

    // Only a REFUSAL tints the count pill red. A package landing or a default-tag mismatch both
    // have something to say, but neither is a failure, and a red pill is the modal's strongest
    // "that did not go in" signal — spending it on a successful scan would blunt it.
    const feedbackTone: ScanFeedbackTone | null = scanFeedback ? TONE[scanFeedback.kind] : null
    const feedbackColor =
      feedbackTone === 'error'
        ? theme.palette.error.main
        : feedbackTone === 'warning'
        ? theme.palette.warning.main
        : 'text.secondary'
    const showErrorTint = feedbackTone === 'error'

    // VRG-233 — group mode shows the CURRENT group's count (caller-supplied), not the
    // whole session's running total. Layered ON TOP of main's `totalScanned`, so the
    // fallback inherits main's counting rule (pending + created; errors and
    // already-certified tags are not tags this batch will mint).
    const displayCount = groupLabel ? (groupCount ?? totalScanned) : totalScanned
    // VRG-233 — group mode gets its own copy (the previously-dead 'group-active' /
    // 'group-inactive' keys), so the hint correctly says "Add Next Group" instead of
    // "Create Certificates".
    const messageKey = groupLabel
      ? (tagScanState === 'active' ? 'group-active' : 'group-inactive')
      : tagScanState

    return (
      <>
      {showMethodSelector && (
      // VRG-231 review item 7 — this rendered as bare Fragment siblings (no
      // spacing between the title and the option rows, and none between the
      // two rows themselves). ReceivePackageModal's own "Scan sources" block
      // avoids this because its whole body sits inside an outer
      // `<Stack spacing={4}>`; this component has no such wrapper, so the gap
      // has to be added here explicitly. 16px (spacing={4} on @repo/ui's 4px
      // base, §29) matches that reference.
      <Stack spacing={4}>
      {/* Section title, consistent with the modal's "Selected product" / "Tags scanned"
          headings. "Sources", not "method": both rows below are live at once. */}
      <Typography variant="body2">Scan sources</Typography>
      <Stack sx={{ py: 1, px: 4, ...scanSourceSurfaceSx(theme, { interactive: true }) }}>
        {/* The WHOLE row toggles the QR panel, not just the "Show QR" link. The row carries a
            radio-style status dot, so it reads as a selectable source and operators click the
            label — which did nothing at all. "Show QR" survives as the visible affordance for
            the same action (it is what says "this opens"), it is simply no longer the only
            thing you can hit. Mirrors the hardware-reader row below, which already worked this
            way. */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          display="flex"
          role="button"
          tabIndex={0}
          aria-expanded={qrOpen}
          onClick={() => setQrExpandedByUser(!qrOpen)}
          onKeyDown={onRowTriggerKeyDown(() => setQrExpandedByUser(!qrOpen))}
          sx={sourceRowTriggerSx()}
        >
          <Stack direction="row" spacing={2} alignItems="center" display="flex">
            <SourceStatus connected={phoneConnected} />
            <Typography variant="body2">Scan with your smartphone</Typography>
            {/* No glossary term covers "pair a phone to a scan session by QR", so this is a
                literal `text` body — the control explanation case the prop exists for. The two
                facts a first-timer cannot infer from the row: the QR is the handoff, and the
                phone needs the Vera app (the QR resolves to a deep link, with a store card as
                the fallback when the app is absent — see the API's scan-app-launch page). */}
            <InfoTooltip text="Scan the QR code with your phone camera to pair that phone with this session. It opens the Vera app, which has to be installed on the phone.">
              <Box
                component="span"
                onClick={stopRowToggle}
                tabIndex={0}
                aria-label="How phone scanning works"
                sx={{ display: 'flex', cursor: 'help' }}
              >
                <InfoIcon strokeWidth={1} size={18} color={theme.palette.grey[800]}/>
              </Box>
            </InfoTooltip>
          </Stack>
          <Stack direction="row" spacing={4} alignItems="center">
            {/* The Show/Hide control renders in BOTH states. It used to live only in the
                not-connected branch, so pairing a phone REPLACED the toggle with the label —
                and since nothing ever sets phoneConnected back to false (no disconnect event,
                no heartbeat), a phone that was backgrounded or killed latched "Connected"
                with the QR hidden and no control to re-show it for re-pairing. */}
            <Stack direction="row" spacing={3} alignItems="center">
              <Link
                component="button"
                type="button"
                underline="none"
                variant="body3"
                // Stopped, or the click toggles here AND again on the row it bubbles into,
                // leaving the panel exactly as it was.
                onClick={e => { e.stopPropagation(); setQrExpandedByUser(!qrOpen) }}
                sx={{ color: 'text.secondary', '&:hover': { color: theme.palette.brand[600] } }}
              >
                {qrOpen ? 'Hide QR' : 'Show QR'}
              </Link>
              {/* TEXT, no glyph. This row carried an NFC mark beside the word "Connected"
                  and a QR mark beside a "Show QR" link — each one restating the word next to
                  it, and the QR one literally saying the same thing as the link it sat
                  beside. Neither carried information its own label did not.

                  The disconnected state says so rather than showing nothing, so the two
                  states are symmetric and the row always answers "is my phone paired?"
                  without the reader having to notice which glyph is missing. */}
              <Typography
                variant="body3"
                color={phoneConnected ? theme.palette.brand[500] : 'text.secondary'}
              >
                {phoneConnected ? 'Connected' : 'Not connected'}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Collapse in={qrOpen}>
          <Divider/>
          <Stack spacing={4} sx={{ minHeight: 218, pt: 4, pb: 4}}>
            {scanDeviceURL && 
              <ScanSessionQRCode registerDeviceURL={scanDeviceURL}/>
            }
          </Stack>
        </Collapse>
      </Stack>
      {/* Rest border via the shared helper — this row had drifted to `grey[300]` while the phone
          row above used `primary[100]`, so the two source rows in ONE modal carried two different
          greys. §34's rest border is `primary[100]`. */}
      {showEncoderOption && (
      <Stack sx={{ py: 1, px: 4, ...scanSourceSurfaceSx(theme, { interactive: true }) }}>
        {/* The whole row is the accordion trigger — a 42px bar with one obvious purpose beats
            a small chevron hit-target beside it. The chevron stays as the AFFORDANCE (it is
            what says "this opens"), it just isn't the only thing you can hit. */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          display="flex"
          role="button"
          tabIndex={0}
          aria-expanded={readerExpanded}
          onClick={() => setReaderExpanded(v => !v)}
          onKeyDown={onRowTriggerKeyDown(() => setReaderExpanded(v => !v))}
          sx={sourceRowTriggerSx()}
        >
          <Stack direction="row" spacing={2} alignItems="center" display="flex">
            <SourceStatus connected={readerConnected} />
            <Typography variant="body2">Scan with a hardware reader</Typography>
            {/* `term` AND `text`: the glossary already defines what a reader IS, so the footer
                links there rather than this call site retyping it (§44). The body says the local
                thing the definition cannot — that this row is about a reader attached to THIS
                computer, and what its status word means. That combination is exactly the
                override `InfoTooltip` documents. */}
            <InfoTooltip
              term="reader"
              text="A Vera desk reader plugged into this computer, reached through the Vera Reader Helper. Not connected means no reader is plugged in, or the helper is not running."
            >
              <Box
                component="span"
                onClick={stopRowToggle}
                tabIndex={0}
                aria-label="How reader scanning works"
                sx={{ display: 'flex', cursor: 'help' }}
              >
                <InfoIcon strokeWidth={1} size={18} color={theme.palette.grey[800]}/>
              </Box>
            </InfoTooltip>
          </Stack>
          <Stack direction="row" spacing={4} alignItems="center">
            {readerConnected && (
              <Typography variant="body3" color={theme.palette.brand[500]}>Connected</Typography>
            )}
            <ChevronDownIcon
              strokeWidth={1}
              size={20}
              style={{
                transform: readerExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease-in-out',
              }}
            />
          </Stack>
        </Stack>
        {/* Setup panel shows only while NOT connected (plug-in guidance / buy / learn).
            Once connected the row stays compact and scan feedback lives in "Tags scanned"
            below — it does not re-expand while scanning. */}
        {/* Operator-controlled now, not `!readerConnected`: this is setup guidance, and being
            unable to dismiss it in the no-reader case (the common one) cost ~260px of modal
            permanently. Reused the shared EmptyState instead of the hand-rolled circle + text
            + buttons this had, which was the same composition at different sizes — the empty
            states on the list pages are the house treatment for exactly this (§28). */}
        <Collapse in={readerExpanded}>
          <Stack>
            <Divider/>
            {/* The panel itself is SHARED with ScanConnectModal (§36) — the same guidance was
                about to be hand-rolled a second time there. Only the accordion around it is
                per-surface, and the panel owns its own vertical padding. */}
            <ReaderSetupPanel connected={readerConnected} />
          </Stack>
        </Collapse>
      </Stack>
      )}
      </Stack>
      )}

      {canShowScanSummary && (
        <>
          {/* Created / Existing / Pending / Errors is engineering telemetry, not for end
              users — hidden by default, shown only when VITE_SCAN_DEBUG=true (dev/debug).
              The user-facing scan feedback is the inline note + the "Tags scanned"
              total below (no per-scan toasts). */}
          {import.meta.env.VITE_SCAN_DEBUG === 'true' && (
            <Stack direction="row" spacing={3} justifyContent="center" alignItems="center" sx={{mb: 2}}>
              <Stack direction="row" spacing={1} alignItems="center"><Typography variant="body3">Created:</Typography><Typography variant="body3" color="success.main">{createdCount}</Typography></Stack>
              <Stack direction="row" spacing={1} alignItems="center"><Typography variant="body3">Existing:</Typography><Typography variant="body3" color="text.primary">{existingCount}</Typography></Stack>
              <Stack direction="row" spacing={1} alignItems="center"><Typography variant="body3">Pending:</Typography><Typography variant="body3" color="warning.main">{pendingCount}</Typography></Stack>
              <Stack direction="row" spacing={1} alignItems="center"><Typography variant="body3">Errors:</Typography><Typography variant="body3" color="error.main">{errorCount}</Typography></Stack>
            </Stack>
          )}
          {packages ? (
            <Typography variant="body2">{singleTag ? 'Scan package' : 'Packages scanned'}</Typography>
          ) :(
            <Typography variant="body2">
              {singleTag
                ? (intent === 'replace'
                    ? 'Scan to replace tag'
                    : intent === 'add'
                      ? 'Scan to add tag'
                      : 'Scan tag')
                : scannedCountLabel(purpose)}
            </Typography>
          )}
          <Stack spacing={4} sx={{minHeight: 218}} justifyContent="center" alignItems="center" display="flex">
            {singleTag ? (
              <Stack 
                direction="row" 
                spacing={2} 
                justifyContent="center" 
                alignItems="center"
                sx={{
                  background: theme.palette.grey[100],
                  borderRadius: 45,
                  maxHeight: 56,
                  maxWidth: 56,
                  p: 4,
                }}>
                {/* `single-inactive` and `single-loading` share ONE placeholder deliberately.
                    Their instruction copy is byte-identical ("Hold your phone near the item's
                    tag location…"), so the two are one state as far as the user is concerned —
                    both mean "waiting for you to hold a tag to the reader". A bespoke animation
                    on one of them invents a distinction the words do not make, and a spinner
                    would be worse still: it promises the app is busy, when what is actually
                    pending is a physical act by the person holding the phone. */}
                {(tagScanState == 'single-inactive' || tagScanState == 'single-loading') && (<SmartphoneNfcIcon strokeWidth={1} /> )}
                {tagScanState == 'single-active' && (<CheckIcon color={theme.palette.brand[500]} strokeWidth={1} /> )}
                {tagScanState == 'failed-scan' && (<SmartphoneNfcIcon color={theme.palette.error.main} strokeWidth={1} /> )}
              </Stack>
            ) : (
              <Stack 
                direction="row" 
                spacing={2} 
                justifyContent="center" 
                alignItems="center"
                sx={{
                  background: showErrorTint ? alpha(theme.palette.red[400], .1): theme.palette.grey[100],
                  borderRadius: 50,
                  height: 52,
                  width: groupLabel ? 'auto' : 121,
                  px: groupLabel ? 4 : 0,
                  border: showErrorTint ? `1px solid ${theme.palette.red[400]}` : 'none',
                }}
              >
                {groupLabel ? (
                  <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{groupLabel}</Typography>
                ) : packages ? (
                  <BoxIcon strokeWidth={1} />
                ) : (
                  <TagIcon strokeWidth={1} />
                )}
                <ArrowRightIcon strokeWidth={.5}/>
                  <Stack
                    // Re-key on the count so each new scan re-mounts the pill and replays the
                    // "pop" animation — a clear, per-scan visual confirmation.
                    key={displayCount}
                    sx={{
                      minWidth: 33,
                      height: 28,
                      px: 1.5,
                      borderRadius: 45,
                      margin: 1,
                      marginLeft: 3,
                      // 0-state: a subtle theme overlay (grey[700] rendered as a near-white
                      // circle in dark mode, so the white "0" was illegible). >0: brand gradient.
                      background: displayCount > 0 ? 'linear-gradient(90deg, rgba(130,90,195,1) 0%, rgba(11,10,103,1) 100%)' : alpha(theme.palette.text.primary, 0.12),
                      alignItems: 'center',
                      justifyContent: 'center',
                      '@keyframes scanPop': {
                        '0%': { transform: 'scale(1)' },
                        '35%': { transform: 'scale(1.35)' },
                        '100%': { transform: 'scale(1)' },
                      },
                      // Only animate once there's something to count (no pop on the initial 0).
                      animation: displayCount > 0 ? 'scanPop 0.32s ease-out' : 'none',
                  }}>
                    <Typography color={displayCount > 0 ? 'common.white' : 'text.secondary'} sx={{fontSize: 14, fontWeight: 700, lineHeight: 1}}>{displayCount}</Typography>
                  </Stack>
              </Stack>
            )}
            <Typography
              ref={feedbackRef}
              variant="body3"
              color={feedbackColor}
              sx={{
                maxWidth: 320,
                textAlign: 'center',
                lineHeight: 1.5,
                // The server's refusals put their support sentence on its OWN line; without
                // this the `\n` collapses to a space and the two sentences run together.
                whiteSpace: 'pre-line',
              }}>
              {scanFeedback
                ? <ScanFeedbackMessage message={scanFeedback.message} />
                : tagScanStateMessages[messageKey as keyof typeof tagScanStateMessages]}
              {/* "This tag is already in use with another certificate" is a dead end on its own —
                  the operator's next question is always "with WHICH one?".
                  INLINE, in the message's own sentence, NOT a button on the row below it. It WAS
                  a row below: measured in the browser at 1280x720, the modal body scrolls
                  (scrollHeight 706 vs clientHeight 516) and that button rendered at y=613 — real,
                  32px tall, and 190px below the fold with nothing to suggest it was there. The
                  message was visible; its only way out was not. Inline, the two cannot separate.
                  Matches how this same component already gives refusals a way out (the
                  "contact support" link in ScanFeedbackMessage).
                  A NEW TAB, because navigating away would tear down the live scan session. */}
              {scanFeedback?.kind === 'existing' && scanFeedback.certificateId && (
                <>
                  {' '}
                  <Link
                    component="button"
                    type="button"
                    variant="body3"
                    underline="always"
                    color="inherit"
                    onClick={() =>
                      window.open(ROUTES.CERTIFICATE(scanFeedback.certificateId as string), '_blank', 'noopener')
                    }
                  >
                    View certificate
                  </Link>
                </>
              )}
              {/* VERA-455 — the way BACK to the receipt prompt after cancelling it.
                  Inline, in the message's own sentence, for exactly the reason the "View
                  certificate" link above is: a control on a row below the message renders
                  past the modal's fold and is never seen. Without this, cancelling the prompt
                  would leave the operator with a refusal and no way to resolve it short of
                  re-tapping the tag, which is the dead end this ticket exists to remove. */}
              {scanFeedback?.kind === 'not-received' && scanFeedback.receipt && (
                <>
                  {' '}
                  <Link
                    component="button"
                    type="button"
                    variant="body3"
                    underline="always"
                    color="inherit"
                    onClick={() => setReceiptPrompt(scanFeedback.receipt as ReceiptPrompt)}
                  >
                    Receive package
                  </Link>
                </>
              )}
            </Typography>
            {scanHint}
          </Stack>

          {/* THE REVIEW LIST — and the only way to undo a mis-scan.
              Until this existed, scanning the wrong tag was unrecoverable short of ending the
              session, which threw away every other tag already scanned. Reviewing here, just
              before Create Certificates, is the natural moment to correct one; per-row removal
              subsumes an "undo last" because the newest row is the first.
              Hidden in singleTag mode (replace-a-tag scans exactly one, and re-scanning simply
              replaces it). */}
          {!singleTag && sessionTags.length > 0 && (
            <>
              <Typography variant="body2">Scanned tags</Typography>
              <Stack
                sx={{
                  border: `1px solid ${theme.palette.primary[100]}`,
                  borderRadius: 2,
                  maxHeight: 208,
                  overflowY: 'auto',
                  /*
                    24px from the title, not the parent Stack's 16.

                    Needs `&&&`: this Stack lays its children out with
                    `> :not(style) ~ :not(style) { margin-top }`, a sibling selector that
                    out-specifies a plain `sx` margin — the same trap the Studio logo card
                    documents. A bare `mt` here computes to 16px and does nothing.

                    Why more than the house 16 for this one list: its first row can be a
                    red-outlined refusal, and a heavy red band 16px under the heading read
                    as attached to it — the label looked like part of the alert rather than
                    the name of the list. The extra 8px separates the two.
                  */
                  '&&&': { marginTop: '24px' },
                }}
              >
                {sessionTags.map((tag, index) => {
                  // A tag that already carries a certificate CANNOT be used by this session —
                  // Create Certificates will skip it. That is a refusal, and it was rendering
                  // as quiet grey secondary text, so the one row in the list that needs acting
                  // on read exactly like the rows that are fine. Tinted with the same red the
                  // rest of this file spends on refusals (the count pill's red[400] wash, the
                  // error.main feedback line), and nothing else in the list is tinted, so the
                  // signal stays scarce.
                  const blocked = tag.status === 'existing'
                  return (
                  <Stack
                    key={tag.tagId}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={3}
                    sx={{
                      px: 4,
                      /*
                        48px, AND EVERY ROW GETS IT — the point is that rows do not change
                        height depending on what is in them.

                        A blocked row carries a `View` button, which is 40px because the
                        shared Button has one size (§60 — `size="small"` is never correct on
                        it). At `py: 2` that made a blocked row 56px against a clean row's
                        40, so a scan resolving to `existing` shoved every row beneath it
                        down mid-run. Reported as "the View buttons are too large and
                        expanding these" — the button is not too large, the row was too
                        tight to hold one.

                        4px of padding around a 40px control is exactly 48, so the button
                        now fits with no growth and clean rows match it. The fix is a
                        uniform row, not a smaller button.
                      */
                      py: 1,
                      minHeight: 48,
                      borderTop: index === 0 ? 'none' : `1px solid ${theme.palette.primary[100]}`,
                      backgroundColor: blocked ? alpha(theme.palette.red[400], 0.1) : 'transparent',
                      // A BORDER, not just a wash. The tint alone was doing all the work of
                      // marking the one row in the list that needs acting on, and against the
                      // list's own `primary[100]` hairlines it read as a faint stripe rather
                      // than as an alert — the same weight the rows that are fine carry.
                      //
                      // `outline` with a -1px offset rather than `border`, deliberately: a real
                      // border adds 2px to the row and shifts every row under it the moment a
                      // scan resolves to `existing`, so the list would jump mid-run. An outline
                      // is drawn inside the row's own box and costs no layout at all.
                      outline: blocked ? `1px solid ${theme.palette.red[400]}` : 'none',
                      outlineOffset: '-1px',
                      /*
                        ROUND THE OUTLINE WHERE IT MEETS THE LIST'S OWN CORNERS.

                        Reported as the borders looking "dull or dimmed" at the corners, and
                        they were being CUT. The list is `borderRadius: 2` with
                        `overflowY: auto`, and an overflow container clips to its rounded
                        box — so a square outline on the first or last row had its outer
                        corners sliced off by that curve. Nothing was dimmed; two corners
                        were simply missing, which reads as a rendering fault.

                        An outline follows its element's `border-radius`, so matching the
                        radius on the rows that touch the ends is the whole fix. 7px, not 8:
                        the outline sits 1px inside the row (`outlineOffset: -1px`), so it
                        needs the container's radius minus that inset to stay concentric —
                        the same inner-radius rule the toggle track uses.

                        Middle rows stay square: their edges never reach a curve, and
                        rounding them would put a floating pill in a square list.
                      */
                      ...(blocked && index === 0
                        ? { borderTopLeftRadius: '7px', borderTopRightRadius: '7px' }
                        : {}),
                      ...(blocked && index === sessionTags.length - 1
                        ? { borderBottomLeftRadius: '7px', borderBottomRightRadius: '7px' }
                        : {}),
                    }}
                  >
                    {/* THE SHARED PREVIEW, not a hand-rolled two-line row (§28/§36). This list
                        used to lead with the raw UID as its primary and only text, which made
                        it the one list in the platform that opens with a hex string instead of
                        a name — `PaymentMethodPreview`, `AddressPreview`, `CompanyCell` and
                        `UserBlock` all lead with a name and carry the identifier beneath.

                        The UID stays, on the subline. In this modal the tags are BLANK — no
                        certificate, no owner, and every row shares the modal's one product —
                        so it is the only thing that tells two rows apart, and it is what an
                        operator reads off the screen to find the physical tag an
                        "Assigned already" row is telling them to go and free.

                        Which BOX a row came from moves to the subline beside the UID: both
                        answer "which physical thing is this", so they belong on one line, and
                        the primary line is left to say what the tag IS. */}
                    <TagPreview
                      uid={tag.tagId}
                      capability={tag.capability}
                      // The leading glyph carries the OUTCOME, not the noun. A fixed tag icon
                      // spent the column the eye runs down restating "this is a tag" — which
                      // the label beside it already says — leaving "did this one take?" to be
                      // read row by row on the right. `created`/`pending` are both accepted;
                      // only `existing` is refused.
                      status={blocked ? 'blocked' : 'ok'}
                      // Tint BOTH lines on a refused row rather than only the outcome text on
                      // the right — the tint and outline already mark the row, and a row whose
                      // left half stays default-coloured reads as half-alerting.
                      color={blocked ? theme.palette.error.main : undefined}
                      packageLabel={
                        tag.viaPackage
                          ? tag.packageName ||
                            (tag.packageId ? tag.packageId.slice(0, 8) : 'Package')
                          : undefined
                      }
                    />
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ flexShrink: 0 }}>
                      {/* RIGHT-ALIGNED, against the X rather than against the uid. Left-aligned
                          it read as another attribute of the tag; the row's outcome belongs on
                          the outcome side, where the eye can run one column down the list. */}
                      {blocked && (
                        <Stack direction="row" spacing={2} alignItems="center">
                          <TriangleAlertIcon
                            strokeWidth={1}
                            size={16}
                            color={theme.palette.error.main}
                            style={{ flexShrink: 0 }}
                          />
                          {/* The certificate it is already on is the only useful next step for
                              this row — see the report on why "Add here instead" does NOT apply
                              here. A new tab, because navigating away tears down the live scan
                              session. Falls back to plain text for a row whose certificate id
                              never arrived, rather than rendering a link that goes nowhere. */}
                          {/* The STATUS is text; the ACTION is a button. They were one
                              underlined label doing both jobs — you had to know the words were
                              clickable to discover the certificate, and the row's one real
                              affordance was disguised as a description of its state. */}
                          <Typography variant="body3" color={theme.palette.error.main} noWrap>
                            Assigned already
                          </Typography>
                          {tag.certificateId && (
                            <Button
                              // SECONDARY, not tertiary. A tertiary button is a bare label,
                              // and on a row already tinted red it stopped reading as a
                              // control at all — it looked like more of the alert's own copy,
                              // sitting next to `Assigned already` in the same weight. This
                              // row's whole point is that it needs acting on, so its one way
                              // out has to look pressable against the tint.
                              variant="secondary"
                              onClick={() =>
                                window.open(
                                  ROUTES.CERTIFICATE(tag.certificateId as string),
                                  '_blank',
                                  'noopener',
                                )
                              }
                              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                              View
                            </Button>
                          )}
                        </Stack>
                      )}
                      {/* An `X`, never a trash can, and no confirm: this removes the tag from a
                          SET, it does not destroy anything (working-conventions §38). */}
                      <IconButton
                        size="small"
                        aria-label={`Remove tag ${tag.tagId} from this session`}
                        disabled={removingTagId === tag.tagId}
                        onClick={() => handleRemoveTag(tag.tagId)}
                        sx={{
                          flexShrink: 0,
                          color: 'text.secondary',
                          '&:hover': { color: theme.palette.error.main },
                        }}
                      >
                        <XIcon strokeWidth={1} size={16} />
                      </IconButton>
                    </Stack>
                  </Stack>
                  )
                })}
              </Stack>
            </>
          )}
        </>
      )}

      {/* VERA-455 — the receipt that resolves an `at_vera` refusal, for BOTH scan paths.
          Hosted here rather than in `CreateCertificatesModal` because both refusals surface
          through this block: a single tag arrives on the SSE, a whole box on `addPackage`'s
          reply. One owner means one modal instance and one continuation rule, and it means the
          other consumers of this block (`ReplaceTagModal`) get the way out for free rather than
          each having to wire its own. */}
      <ConfirmReceivePackageModal
        prompt={receiptPrompt}
        onClose={() => setReceiptPrompt(null)}
        onReceived={async (received) => {
          if (receiptPrompt?.source === 'package') {
            // "Then proceed": re-run the expansion that was refused. Nothing landed on the
            // refused attempt, so this is a clean retry, and the SSE fills the list as usual.
            await handleAddPackage(received.packageId).catch((err) => {
              notification({ type: 'error', title: getErrorMessage(err) })
            })
            return
          }
          // Single tag: the tap's proof was consumed by the refused attempt and there is no
          // add-by-uid path, so ask for another tap rather than silently expanding the whole
          // box (~100 tags the operator never scanned). `force` because the operator just
          // acted — this must replace the refusal they were reading.
          showScanFeedback(
            {
              kind: 'package',
              message: `${received.packageName?.trim() || 'Package'} received. Scan the tag again to add it.`,
            },
            { force: true },
          )
        }}
      />
      </>
    )
});
